/**
 * Performance leaderboard row detail page.
 *
 * Mirrors §4.27 of `docs/detail-pages-and-enrollment-plan.md`. The list
 * page (`_authed.hr.performance.tsx`) keeps a pencil shortcut that opens
 * the inline drawer; row click navigates here instead. The detail page
 * composes the existing performance primitives (`ScoreCard`,
 * `VariableBreakdown`, `PeriodReports`, `HrCommentsThread`,
 * `NotifyParentsDrawer`) — no logic is duplicated.
 *
 * RBAC (per the plan):
 *   View              HR_ROLES   = super_admin | academic_admin | nalim
 *   Notify parents    NOTIFY     = super_admin | academic_admin | teacher
 *                                  (nalim excluded — they're internal HR)
 *   Recompute one     super_admin only
 *   Publish report    super_admin only
 *   View audit        super_admin | academic_admin | support_viewer
 *
 * Endpoints consumed (all produced by the backend agent in parallel):
 *   GET  /api/performance/teachers/:id/header                       — bundle
 *   GET  /api/performance/teachers/:id/messages-sent                — paged
 *   GET  /api/performance/teachers/:id/inputs/feedback              — drill-down
 *   GET  /api/performance/teachers/:id/inputs/attendance            — drill-down
 *   GET  /api/performance/teachers/:id/inputs/finance               — drill-down
 *   GET  /api/performance/teachers/:id/audit                        — aggregate
 *   POST /api/performance/teachers/:id/reports/:periodKey/publish   — write
 *   POST /api/performance/snapshots/recompute (existing)            — write
 *
 * The page degrades gracefully while those endpoints land: any 404 / 403
 * collapses to an EmptyState rather than throwing the whole route.
 */
import {
  createFileRoute,
  Link,
  useNavigate,
} from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  RefreshCcw,
  Send,
  Settings,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError, type ListResponse } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Drawer } from '../components/ui/Drawer';
import { TextAreaField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { Sparkline } from '../components/shared/Sparkline';
import { formatDate, timeAgo } from '../lib/format';
import {
  BandChip,
  HrCommentsThread,
  NotifyParentsDrawer,
  PeriodReports,
  ScoreCard,
  VariableBreakdown,
  PERFORMANCE_BAND_LABEL,
  formatVariableRaw,
  type HrCommentsThreadHandle,
  type PerformanceBandValue,
  type PerformanceConfig,
  type PerformanceMetricBreakdown,
  type PerformanceSnapshotResponse,
  type PerformanceTrendResponse,
} from '../components/performance';

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute(
  '/_authed/hr/performance/teachers/$teacherId',
)({
  component: PerformanceTeacherDetailPage,
});

// ---------------------------------------------------------------------------
// Local response shapes
// ---------------------------------------------------------------------------
//
// These mirror the bundle endpoints the backend agent is producing in
// parallel. They reuse the canonical `PerformanceSnapshotResponse` from
// `@bbl/validation` so any backend schema change shows up as a TS error
// here. Fields not yet locked in by the backend are marked optional so
// the page renders even before the endpoint lands.

interface PerformanceTeacherSummary {
  id: string;
  name: string | null;
  status: string | null;
  employeeId: string | null;
  email: string | null;
}

interface PerformanceTeacherHeaderResponse {
  teacher: PerformanceTeacherSummary;
  latestSnapshot: PerformanceSnapshotResponse | null;
  /**
   * Optional — rank within the latest snapshot's band. When present we
   * surface "#3 of 12 in Top" as a sub on the Composite KPI card.
   */
  rankInBand?: {
    rank: number;
    bandSize: number;
    band: PerformanceBandValue;
  } | null;
  /** Active performance_config_history row in effect for the snapshot. */
  activeConfig: PerformanceConfig | null;
  /**
   * Periods that have been published or are publishable. Used by the
   * "Period reports" tab to render a Publish CTA per unpublished period.
   */
  publishablePeriods?: ReadonlyArray<{
    periodKey: string;
    period: 'daily' | 'weekly' | 'monthly';
    label: string;
    publishedAt: string | null;
    publishedBy: string | null;
    snapshotCount: number;
  }>;
}

interface PerformanceMessageSentRow {
  id: string;
  sentAt: string;
  sentBy: { id: string | null; displayName: string | null } | null;
  audienceKind: string | null;
  channel: string | null;
  status: string | null;
  recipientCount: number;
  subject: string | null;
}

interface FeedbackInputResponse {
  responseCount: number;
  axes: Partial<{
    clarity: number | null;
    patience: number | null;
    communication: number | null;
    progress: number | null;
    overall: number | null;
  }>;
  periodKey: string | null;
  weightedScoreX100: number | null;
}

interface AttendanceInputResponse {
  periodStart: string;
  periodEnd: string;
  presentCount: number;
  absentCount: number;
  percentOnTime: number | null;
  attendanceRate: number | null;
}

interface FinanceInputResponse {
  feeCollectionRate: number | null;
  latePaymentsCount: number;
  totalInvoiced: number | null;
  totalCollected: number | null;
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type Tab =
  | 'overview'
  | 'trend'
  | 'inputs'
  | 'period-reports'
  | 'notes'
  | 'messages'
  | 'config'
  | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'trend', label: 'Trend' },
  { value: 'inputs', label: 'Inputs' },
  { value: 'period-reports', label: 'Period reports' },
  { value: 'notes', label: 'Notes' },
  { value: 'messages', label: 'Messages sent' },
  { value: 'config', label: 'Config in effect' },
  { value: 'activity', label: 'Activity' },
];

type TrendWindow = 30 | 60 | 90;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PerformanceTeacherDetailPage(): JSX.Element {
  const { teacherId } = Route.useParams();
  const _navigate = useNavigate();
  const hasRole = useAuthStore((s) => s.hasRole);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // Per §4.27 RBAC matrix.
  const canNotifyParents = hasRole(
    'super_admin',
    'academic_admin',
    'teacher',
  );
  const canRecompute = hasRole('super_admin');
  const canPublish = hasRole('super_admin');
  const canViewAudit = hasRole(
    'super_admin',
    'academic_admin',
    'support_viewer',
  );

  const [tab, setTab] = useState<Tab>('overview');
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState<{
    periodKey: string;
    label: string;
  } | null>(null);
  const [trendWindow, setTrendWindow] = useState<TrendWindow>(30);

  const commentsThreadRef = useRef<HrCommentsThreadHandle | null>(null);

  // -----------------------------------------------------------------------
  // Header bundle (single round-trip)
  // -----------------------------------------------------------------------
  const headerQuery = useQuery({
    queryKey: ['performance-teacher', 'header', teacherId] as const,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceTeacherHeaderResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/header`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const header = headerQuery.data ?? null;
  const teacher = header?.teacher ?? null;
  const latestSnapshot = header?.latestSnapshot ?? null;

  // -----------------------------------------------------------------------
  // Edit pencil — focus the HR notes composer and switch to Notes tab.
  // -----------------------------------------------------------------------
  const editNotes = (): void => {
    setTab('notes');
    // The thread mounts on first switch to Notes; wait a tick.
    window.setTimeout(() => {
      commentsThreadRef.current?.focusComposer();
    }, 50);
  };

  // -----------------------------------------------------------------------
  // Recompute this teacher (super_admin only)
  // -----------------------------------------------------------------------
  const recomputeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ count: number; errors: number; durationMs: number }>(
        '/api/performance/snapshots/recompute',
        { method: 'POST', body: { teacherIds: [teacherId] } },
      ),
    onSuccess: (result) => {
      toast(
        result.errors > 0
          ? `Recomputed with ${result.errors} error${result.errors === 1 ? '' : 's'}.`
          : 'Snapshot recomputed.',
        { variant: result.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({ queryKey: ['performance'] });
      void queryClient.invalidateQueries({
        queryKey: ['performance-teacher'],
      });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError
          ? err.message
          : 'Could not recompute the snapshot.',
        { variant: 'error' },
      ),
  });

  const handleRecompute = async (): Promise<void> => {
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Recompute this teacher?',
      description:
        "Recomputes the latest snapshot using the current configuration. Yesterday's snapshots stay unchanged.",
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    recomputeMutation.mutate();
  };

  // -----------------------------------------------------------------------
  // Header chrome
  // -----------------------------------------------------------------------
  const carryForward =
    latestSnapshot?.parentFeedbackCarryforwardApplied ?? false;

  const statusChip: ReactNode = latestSnapshot ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <BandChip band={latestSnapshot.band} />
      {carryForward ? (
        <span
          className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] font-medium text-[color:var(--color-text-muted)]"
          title="Parent feedback carry-forward applied at 50% weight"
        >
          Carry-forward
        </span>
      ) : null}
    </div>
  ) : null;

  const sublabel = useMemo<ReactNode>(() => {
    const parts: string[] = ['Performance'];
    if (latestSnapshot?.snapshotDate) {
      parts.push(formatDate(latestSnapshot.snapshotDate));
    }
    if (teacher?.employeeId) {
      parts.push(`#${teacher.employeeId}`);
    }
    return parts.join(' · ');
  }, [latestSnapshot, teacher]);

  // -----------------------------------------------------------------------
  // KPI strip — six cards driven by the canonical snapshot fields
  // -----------------------------------------------------------------------
  const kpiItems = useMemo<KpiItem[]>(() => {
    const m = latestSnapshot?.metrics ?? null;
    const attendanceMetric = m?.attendance_rate ?? null;
    const examMetric = m?.student_exam_avg ?? null;
    const feeMetric = m?.fee_collection_rate ?? null;
    const rank = header?.rankInBand ?? null;

    const compositeSub =
      rank && rank.band === latestSnapshot?.band
        ? `#${rank.rank} of ${rank.bandSize} in ${PERFORMANCE_BAND_LABEL[rank.band]}`
        : latestSnapshot
          ? `${latestSnapshot.periodDays}-day window`
          : 'no snapshot yet';

    const lastSub = latestSnapshot?.snapshotDate
      ? formatDate(latestSnapshot.snapshotDate)
      : '—';
    const lastValue = latestSnapshot?.snapshotDate
      ? timeAgo(latestSnapshot.snapshotDate)
      : '—';

    return [
      {
        label: 'Composite score',
        value: latestSnapshot ? `${latestSnapshot.score}/100` : '—',
        sub: compositeSub,
      },
      {
        label: 'Attendance rate',
        value: formatMetricRaw('attendance_rate', attendanceMetric),
        sub: subForMetric(attendanceMetric),
      },
      {
        label: 'Student exam avg',
        value: formatMetricRaw('student_exam_avg', examMetric),
        sub: subForMetric(examMetric),
      },
      {
        label: 'Fee collection',
        value: formatMetricRaw('fee_collection_rate', feeMetric),
        sub: subForMetric(feeMetric),
      },
      {
        label: 'Active teaching days',
        value: latestSnapshot?.activeTeachingDays ?? '—',
        sub: latestSnapshot
          ? `over last ${latestSnapshot.periodDays} days`
          : '—',
      },
      {
        label: 'Last snapshot',
        value: lastValue,
        sub: lastSub,
      },
    ];
  }, [header, latestSnapshot]);

  // -----------------------------------------------------------------------
  // Loading / error gating
  // -----------------------------------------------------------------------
  if (headerQuery.isLoading && !header) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl space-y-4 px-4 py-6 sm:px-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-6 sm:px-6">
      <GatedSection
        error={headerQuery.error}
        title="Performance scores"
        onRetry={() => headerQuery.refetch()}
      >
        <div className="space-y-6">
          <DetailHeader
            title={teacher?.name ?? 'Teacher'}
            sublabel={sublabel}
            statusChip={statusChip}
            backTo="/hr/performance"
            actions={
              <>
                {canNotifyParents ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Send size={14} aria-hidden="true" />}
                    onClick={() => setNotifyOpen(true)}
                  >
                    Notify parents
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<MessageCircle size={14} aria-hidden="true" />}
                  onClick={editNotes}
                >
                  Add HR comment
                </Button>
                <button
                  type="button"
                  onClick={editNotes}
                  aria-label="Edit notes & alerts"
                  title="Edit notes & alerts"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-text-muted)] transition-colors hover:border-gold-500 hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
                <OverflowMenu
                  open={overflowOpen}
                  onOpenChange={setOverflowOpen}
                  items={[
                    ...(canRecompute
                      ? [
                          {
                            label: 'Recompute this teacher',
                            icon: (
                              <RefreshCcw size={14} aria-hidden="true" />
                            ),
                            onSelect: () => {
                              void handleRecompute();
                            },
                            disabled: recomputeMutation.isPending,
                          },
                        ]
                      : []),
                    ...(canViewAudit
                      ? [
                          {
                            label: 'View full activity history',
                            icon: (
                              <ExternalLink size={14} aria-hidden="true" />
                            ),
                            onSelect: () => {
                              setOverflowOpen(false);
                              setTab('activity');
                            },
                          },
                        ]
                      : []),
                    {
                      label: 'Export this teacher (CSV)',
                      icon: <Download size={14} aria-hidden="true" />,
                      onSelect: () => {
                        setOverflowOpen(false);
                        downloadTeacherExport(teacherId);
                      },
                    },
                  ]}
                />
              </>
            }
          />

          <KpiStrip items={kpiItems} loading={headerQuery.isLoading} />

          <div
            role="tablist"
            aria-label="Performance sections"
            className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
          >
            {TAB_OPTIONS.map((opt) => {
              if (opt.value === 'activity' && !canViewAudit) return null;
              const active = tab === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(opt.value)}
                  className={clsx(
                    'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                    active
                      ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                      : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {tab === 'overview' ? (
            <OverviewTab snapshot={latestSnapshot} />
          ) : null}

          {tab === 'trend' ? (
            <TrendTab
              teacherId={teacherId}
              trendWindow={trendWindow}
              onWindowChange={setTrendWindow}
              teacherName={teacher?.name ?? null}
            />
          ) : null}

          {tab === 'inputs' ? <InputsTab teacherId={teacherId} /> : null}

          {tab === 'period-reports' ? (
            <PeriodReportsTab
              teacherId={teacherId}
              publishablePeriods={header?.publishablePeriods ?? []}
              canPublish={canPublish}
              onPublishClick={(p) => setPublishTarget(p)}
            />
          ) : null}

          {tab === 'notes' ? (
            <HrCommentsThread ref={commentsThreadRef} teacherId={teacherId} />
          ) : null}

          {tab === 'messages' ? (
            <MessagesSentTab teacherId={teacherId} />
          ) : null}

          {tab === 'config' ? (
            <ConfigInEffectTab config={header?.activeConfig ?? null} />
          ) : null}

          {tab === 'activity' && canViewAudit ? (
            <ActivityPanel
              targetType="teacher"
              targetId={teacherId}
              title="Activity"
              emptyCopy="No performance events for this teacher yet."
            />
          ) : null}
        </div>
      </GatedSection>

      <NotifyParentsDrawer
        open={notifyOpen}
        teacherId={teacherId}
        onClose={() => setNotifyOpen(false)}
      />

      <PublishPeriodDrawer
        teacherId={teacherId}
        target={publishTarget}
        onClose={() => setPublishTarget(null)}
        onPublished={() => {
          setPublishTarget(null);
          void queryClient.invalidateQueries({
            queryKey: ['performance-teacher', 'header', teacherId],
          });
          void queryClient.invalidateQueries({
            queryKey: ['performance', 'reports', teacherId],
          });
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — KPI formatting
// ---------------------------------------------------------------------------

const formatMetricRaw = (
  key:
    | 'attendance_rate'
    | 'student_exam_avg'
    | 'fee_collection_rate',
  metric: PerformanceMetricBreakdown | null,
): string => {
  if (!metric) return '—';
  return formatVariableRaw(key, metric.raw, metric.dataPresent);
};

const subForMetric = (
  metric: PerformanceMetricBreakdown | null,
): string => {
  if (!metric) return 'no data';
  if (!metric.included) return 'excluded';
  if (!metric.dataPresent) return 'no data';
  return `${Math.round(metric.normalized)} / 100`;
};

const downloadTeacherExport = (teacherId: string): void => {
  const apiOrigin =
    (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
    'https://api.babulilmonline.com';
  // Existing export endpoint accepts a snapshotDate; we fall through to
  // the latest snapshot. A per-teacher filter exists on the same endpoint
  // via `?teacherIds=` for HR exports; if the backend hasn't added that
  // yet the file simply contains all teachers and the operator filters
  // client-side.
  const url = `${apiOrigin}/api/performance/export?teacherIds=${encodeURIComponent(teacherId)}`;
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '';
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

function OverviewTab({
  snapshot,
}: {
  snapshot: PerformanceSnapshotResponse | null;
}): JSX.Element {
  if (!snapshot) {
    return (
      <EmptyState
        title="No snapshot yet"
        description="Once the nightly cron writes its first snapshot, the breakdown will appear here."
      />
    );
  }
  return (
    <section className="space-y-4">
      <ScoreCard snapshot={snapshot} />
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <header className="mb-3">
          <h3 className="font-display text-sm font-semibold">
            Breakdown by metric
          </h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Each row shows the raw value, the 0–100 normalized score, and
            whether the metric contributed to the composite this period.
          </p>
        </header>
        <VariableBreakdown metrics={snapshot.metrics} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab: Trend
// ---------------------------------------------------------------------------

interface TrendTabProps {
  teacherId: string;
  trendWindow: TrendWindow;
  onWindowChange: (w: TrendWindow) => void;
  teacherName: string | null;
}

const TREND_WINDOWS: ReadonlyArray<TrendWindow> = [30, 60, 90];

function TrendTab({
  teacherId,
  trendWindow,
  onWindowChange,
  teacherName,
}: TrendTabProps): JSX.Element {
  const trendQuery = useQuery({
    queryKey: [
      'performance',
      'trend',
      teacherId,
      trendWindow,
    ] as const,
    queryFn: ({ signal }) =>
      apiRequest<PerformanceTrendResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/trend`,
        { signal, query: { days: trendWindow } },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const items = trendQuery.data?.items ?? [];

  // Detect band transitions so we can annotate them as labelled rings on
  // the sparkline title (the Sparkline primitive only supports horizontal
  // gridlines + per-point hover titles — we surface transitions via
  // titles rather than vertical markers).
  const sortedItems = useMemo(() => {
    return items
      .slice()
      .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));
  }, [items]);

  const sparkPoints = useMemo(() => {
    let prevBand: PerformanceBandValue | null = null;
    return sortedItems.map((item) => {
      const isTransition =
        prevBand !== null && prevBand !== item.band;
      const titleParts = [
        formatDate(item.snapshotDate),
        `${item.score}/100`,
        PERFORMANCE_BAND_LABEL[item.band],
      ];
      if (isTransition && prevBand !== null) {
        titleParts.push(
          `band changed from ${PERFORMANCE_BAND_LABEL[prevBand]}`,
        );
      }
      if (item.parentFeedbackCarryforwardApplied) {
        titleParts.push('feedback carry-forward');
      }
      prevBand = item.band;
      return {
        x: item.snapshotDate,
        y: item.score,
        title: titleParts.join(' · '),
      };
    });
  }, [sortedItems]);

  const transitionCount = useMemo(() => {
    let count = 0;
    let prev: PerformanceBandValue | null = null;
    for (const item of sortedItems) {
      if (prev !== null && prev !== item.band) count += 1;
      prev = item.band;
    }
    return count;
  }, [sortedItems]);

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-sm font-semibold">
            Composite score trend
          </h3>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Daily 0–100 score over the selected window.
            {transitionCount > 0
              ? ` ${transitionCount} band transition${transitionCount === 1 ? '' : 's'} in this window.`
              : ''}
          </p>
        </div>
        <div
          role="tablist"
          aria-label="Trend window"
          className="inline-flex rounded-md border border-[color:var(--color-border)] p-0.5"
        >
          {TREND_WINDOWS.map((w) => {
            const active = w === trendWindow;
            return (
              <button
                key={w}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => onWindowChange(w)}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors',
                  active
                    ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/30 dark:text-gold-50'
                    : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                )}
              >
                {w} days
              </button>
            );
          })}
        </div>
      </header>

      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        {trendQuery.isLoading && !trendQuery.data ? (
          <Skeleton className="h-40 w-full" />
        ) : sparkPoints.length === 0 ? (
          <EmptyState
            title="No trend yet"
            description="Trend builds up once the nightly snapshot cron has written at least two days for this teacher."
          />
        ) : (
          <Sparkline
            points={sparkPoints}
            gridlines={[33, 66]}
            ariaLabel={`Composite score trend for ${teacherName ?? 'teacher'}`}
          />
        )}
      </div>

      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <PeriodReports teacherId={teacherId} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab: Inputs (drill-down)
// ---------------------------------------------------------------------------

function InputsTab({ teacherId }: { teacherId: string }): JSX.Element {
  return (
    <section className="space-y-3">
      <FeedbackInputCard teacherId={teacherId} />
      <AttendanceInputCard teacherId={teacherId} />
      <FinanceInputCard teacherId={teacherId} />
    </section>
  );
}

interface CollapsibleCardProps {
  title: string;
  description: string;
  defaultOpen?: boolean;
  rightSlot?: ReactNode;
  /**
   * Fired exactly once when the card first opens. Lets parents lazily
   * arm their query on first expand without writing to state during
   * render.
   */
  onFirstOpen?: () => void;
  children: ReactNode;
}

function CollapsibleCard({
  title,
  description,
  defaultOpen = false,
  rightSlot,
  onFirstOpen,
  children,
}: CollapsibleCardProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const firedRef = useRef(false);

  useEffect(() => {
    if (open && !firedRef.current) {
      firedRef.current = true;
      onFirstOpen?.();
    }
  }, [open, onFirstOpen]);

  return (
    <article className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <header className="flex items-center justify-between gap-3 px-5 py-3">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {open ? (
            <ChevronDown
              size={14}
              aria-hidden="true"
              className="shrink-0 text-[color:var(--color-text-muted)]"
            />
          ) : (
            <ChevronRight
              size={14}
              aria-hidden="true"
              className="shrink-0 text-[color:var(--color-text-muted)]"
            />
          )}
          <div className="min-w-0">
            <p className="font-display text-sm font-semibold">{title}</p>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              {description}
            </p>
          </div>
        </button>
        {rightSlot ? <div className="shrink-0">{rightSlot}</div> : null}
      </header>
      {open ? (
        <div className="border-t border-[color:var(--color-border)] px-5 py-4">
          {children}
        </div>
      ) : null}
    </article>
  );
}

function FeedbackInputCard({ teacherId }: { teacherId: string }): JSX.Element {
  const [opened, setOpened] = useState(false);

  const query = useQuery({
    queryKey: ['performance-teacher', 'inputs', 'feedback', teacherId] as const,
    enabled: opened,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackInputResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/inputs/feedback`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  return (
    <CollapsibleCard
      title="Feedback signals"
      description="Per-axis parent feedback averages and response volume."
      onFirstOpen={() => setOpened(true)}
      rightSlot={
        <Link
          to="/hr/feedback/teachers/$teacherId"
          params={{ teacherId }}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-[11px] font-medium text-[color:var(--color-text)] hover:border-gold-500"
        >
          <ExternalLink size={11} aria-hidden="true" />
          Open feedback
        </Link>
      }
    >
      {renderFeedbackBody(query)}
    </CollapsibleCard>
  );
}

function renderFeedbackBody(
  query: ReturnType<typeof useQuery<FeedbackInputResponse, unknown>>,
): ReactNode {
  if (query.isLoading || !query.data) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (query.error) {
    return (
      <EmptyState
        title="Could not load feedback signals"
        description={
          query.error instanceof ApiError
            ? query.error.message
            : 'Try again later.'
        }
      />
    );
  }
  const data = query.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-xs">
        <Stat
          label="Responses"
          value={data.responseCount.toLocaleString()}
        />
        <Stat label="Period" value={data.periodKey ?? '—'} />
        <Stat
          label="Weighted (x100)"
          value={
            data.weightedScoreX100 !== null
              ? data.weightedScoreX100.toFixed(0)
              : '—'
          }
        />
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">
        <AxisStat label="Clarity" value={data.axes.clarity ?? null} />
        <AxisStat label="Patience" value={data.axes.patience ?? null} />
        <AxisStat
          label="Communication"
          value={data.axes.communication ?? null}
        />
        <AxisStat label="Progress" value={data.axes.progress ?? null} />
        <AxisStat label="Overall" value={data.axes.overall ?? null} />
      </dl>
    </div>
  );
}

function AttendanceInputCard({
  teacherId,
}: {
  teacherId: string;
}): JSX.Element {
  const [opened, setOpened] = useState(false);

  const query = useQuery({
    queryKey: [
      'performance-teacher',
      'inputs',
      'attendance',
      teacherId,
    ] as const,
    enabled: opened,
    queryFn: ({ signal }) =>
      apiRequest<AttendanceInputResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/inputs/attendance`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  return (
    <CollapsibleCard
      title="Attendance signals"
      description="Class attendance over the rolling window."
      onFirstOpen={() => setOpened(true)}
    >
      {renderAttendanceBody(query)}
    </CollapsibleCard>
  );
}

function renderAttendanceBody(
  query: ReturnType<typeof useQuery<AttendanceInputResponse, unknown>>,
): ReactNode {
  if (query.isLoading || !query.data) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (query.error) {
    return (
      <EmptyState
        title="Could not load attendance signals"
        description={
          query.error instanceof ApiError
            ? query.error.message
            : 'Try again later.'
        }
      />
    );
  }
  const data = query.data;
  return (
    <div className="flex flex-wrap gap-4 text-xs">
      <Stat
        label="Period"
        value={`${formatDate(data.periodStart)} → ${formatDate(data.periodEnd)}`}
      />
      <Stat label="Present" value={data.presentCount.toLocaleString()} />
      <Stat label="Absent" value={data.absentCount.toLocaleString()} />
      <Stat
        label="Attendance rate"
        value={
          data.attendanceRate !== null
            ? `${data.attendanceRate.toFixed(1)}%`
            : '—'
        }
      />
      <Stat
        label="On-time"
        value={
          data.percentOnTime !== null
            ? `${data.percentOnTime.toFixed(1)}%`
            : '—'
        }
      />
    </div>
  );
}

function FinanceInputCard({
  teacherId,
}: {
  teacherId: string;
}): JSX.Element {
  const [opened, setOpened] = useState(false);

  const query = useQuery({
    queryKey: [
      'performance-teacher',
      'inputs',
      'finance',
      teacherId,
    ] as const,
    enabled: opened,
    queryFn: ({ signal }) =>
      apiRequest<FinanceInputResponse>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/inputs/finance`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  return (
    <CollapsibleCard
      title="Finance signals"
      description="Fee collection rate and late payment counts."
      onFirstOpen={() => setOpened(true)}
    >
      {renderFinanceBody(query)}
    </CollapsibleCard>
  );
}

function renderFinanceBody(
  query: ReturnType<typeof useQuery<FinanceInputResponse, unknown>>,
): ReactNode {
  if (query.isLoading || !query.data) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (query.error) {
    return (
      <EmptyState
        title="Could not load finance signals"
        description={
          query.error instanceof ApiError
            ? query.error.message
            : 'Try again later.'
        }
      />
    );
  }
  const data = query.data;
  return (
    <div className="flex flex-wrap gap-4 text-xs">
      <Stat
        label="Collection rate"
        value={
          data.feeCollectionRate !== null
            ? `${data.feeCollectionRate.toFixed(1)}%`
            : '—'
        }
      />
      <Stat
        label="Late payments"
        value={data.latePaymentsCount.toLocaleString()}
      />
      {data.totalInvoiced !== null ? (
        <Stat
          label="Invoiced"
          value={data.totalInvoiced.toLocaleString()}
        />
      ) : null}
      {data.totalCollected !== null ? (
        <Stat
          label="Collected"
          value={data.totalCollected.toLocaleString()}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Period reports
// ---------------------------------------------------------------------------

interface PeriodReportsTabProps {
  teacherId: string;
  publishablePeriods: ReadonlyArray<{
    periodKey: string;
    period: 'daily' | 'weekly' | 'monthly';
    label: string;
    publishedAt: string | null;
    publishedBy: string | null;
    snapshotCount: number;
  }>;
  canPublish: boolean;
  onPublishClick: (target: { periodKey: string; label: string }) => void;
}

function PeriodReportsTab({
  teacherId,
  publishablePeriods,
  canPublish,
  onPublishClick,
}: PeriodReportsTabProps): JSX.Element {
  const unpublished = publishablePeriods.filter((p) => p.publishedAt === null);
  const published = publishablePeriods.filter((p) => p.publishedAt !== null);

  return (
    <section className="space-y-5">
      {publishablePeriods.length > 0 ? (
        <article className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
          <header className="mb-3 flex items-baseline justify-between gap-3">
            <div>
              <h3 className="font-display text-sm font-semibold">
                Publishable periods
              </h3>
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {canPublish
                  ? 'Publishing freezes the period report and notifies the teacher. Unpublished periods stay HR-only.'
                  : 'Only super-admins can publish a period report.'}
              </p>
            </div>
            <FileText
              size={16}
              aria-hidden="true"
              className="text-[color:var(--color-text-muted)]"
            />
          </header>
          <ul className="divide-y divide-[color:var(--color-border)]">
            {unpublished.length > 0
              ? unpublished.map((p) => (
                  <li
                    key={p.periodKey}
                    className="flex flex-wrap items-center justify-between gap-2 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{p.label}</p>
                      <p className="text-[11px] text-[color:var(--color-text-muted)]">
                        {p.snapshotCount} snapshot
                        {p.snapshotCount === 1 ? '' : 's'} · {p.period}
                      </p>
                    </div>
                    {canPublish ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          onPublishClick({
                            periodKey: p.periodKey,
                            label: p.label,
                          })
                        }
                      >
                        Publish
                      </Button>
                    ) : (
                      <span className="text-[11px] text-[color:var(--color-text-muted)]">
                        Awaiting super-admin
                      </span>
                    )}
                  </li>
                ))
              : (
                <li className="py-3 text-xs text-[color:var(--color-text-muted)]">
                  All eligible periods have been published.
                </li>
              )}
          </ul>
          {published.length > 0 ? (
            <details className="mt-3">
              <summary className="cursor-pointer select-none text-xs text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]">
                {published.length} previously published period
                {published.length === 1 ? '' : 's'}
              </summary>
              <ul className="mt-2 divide-y divide-[color:var(--color-border)]">
                {published.map((p) => (
                  <li
                    key={p.periodKey}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs"
                  >
                    <span className="font-medium">{p.label}</span>
                    <span className="inline-flex items-center gap-1 text-[color:var(--color-text-muted)]">
                      <CheckCircle2 size={12} aria-hidden="true" />
                      Published{' '}
                      {p.publishedAt
                        ? timeAgo(p.publishedAt)
                        : '—'}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </article>
      ) : null}

      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <PeriodReports teacherId={teacherId} />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Publish drawer (super_admin only)
// ---------------------------------------------------------------------------

interface PublishPeriodDrawerProps {
  teacherId: string;
  target: { periodKey: string; label: string } | null;
  onClose: () => void;
  onPublished: () => void;
}

function PublishPeriodDrawer({
  teacherId,
  target,
  onClose,
  onPublished,
}: PublishPeriodDrawerProps): JSX.Element {
  const { toast } = useToast();
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!target) setNotes('');
  }, [target]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error('No publish target selected.');
      const body: Record<string, string> = {};
      const trimmed = notes.trim();
      if (trimmed.length > 0) body.notes = trimmed;
      return apiRequest<{ ok: true }>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/reports/${encodeURIComponent(target.periodKey)}/publish`,
        { method: 'POST', body },
      );
    },
    onSuccess: () => {
      toast(`Period ${target?.label ?? ''} published.`.trim(), {
        variant: 'success',
      });
      onPublished();
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not publish the period.',
        { variant: 'error' },
      ),
  });

  return (
    <Drawer
      open={target !== null}
      onClose={onClose}
      size="sm"
      title={target ? `Publish ${target.label}` : 'Publish period'}
    >
      {target ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <p className="text-sm text-[color:var(--color-text-muted)]">
            Publishing freezes the report for{' '}
            <span className="font-medium text-[color:var(--color-text)]">
              {target.label}
            </span>{' '}
            and surfaces it to the teacher. Unpublished snapshots stay
            HR-only.
          </p>

          <TextAreaField
            label="Notes (optional)"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Context for the teacher — coaching points, what HR saw this period."
            disabled={mutation.isPending}
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Publish
            </Button>
          </div>
        </form>
      ) : null}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Tab: Messages sent
// ---------------------------------------------------------------------------

function MessagesSentTab({
  teacherId,
}: {
  teacherId: string;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const query = useQuery({
    queryKey: [
      'performance-teacher',
      'messages-sent',
      teacherId,
      page,
    ] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<PerformanceMessageSentRow>>(
        `/api/performance/teachers/${encodeURIComponent(teacherId)}/messages-sent`,
        { signal, query: { page, pageSize: PAGE_SIZE } },
      ),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  if (query.isLoading && !query.data) {
    return <Skeleton className="h-64 w-full" />;
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No messages sent for this teacher yet"
        description="Once you (or another admin) send a parent broadcast scoped to this teacher, those messages appear here."
      />
    );
  }
  const total = query.data?.total ?? items.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--color-surface)]">
              <tr>
                <Th>Sent</Th>
                <Th>By</Th>
                <Th>Subject</Th>
                <Th>Audience</Th>
                <Th>Channel</Th>
                <Th>Status</Th>
                <Th align="right">Recipients</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-[color:var(--color-border)]"
                >
                  <td className="whitespace-nowrap px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {formatDate(row.sentAt, { withTime: true })}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    {row.sentBy?.displayName ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <p className="line-clamp-1 max-w-[32ch] text-sm">
                      {row.subject ?? '—'}
                    </p>
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {row.audienceKind ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {row.channel ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                    {row.status ?? '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums">
                    {row.recipientCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 ? (
        <nav
          aria-label="Messages sent pagination"
          className="flex items-center justify-end gap-2"
        >
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <span className="text-xs tabular-nums text-[color:var(--color-text-muted)]">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab: Config in effect
// ---------------------------------------------------------------------------

function ConfigInEffectTab({
  config,
}: {
  config: PerformanceConfig | null;
}): JSX.Element {
  if (!config) {
    return (
      <EmptyState
        title="No active configuration"
        description="The default config will be seeded on the first read or first PATCH."
      />
    );
  }
  const includedKeys = Object.entries(config.includeFlags)
    .filter(([, included]) => included)
    .map(([key]) => key);

  return (
    <section className="space-y-4">
      <article className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Active configuration
            </p>
            <p className="font-display text-base font-semibold">
              Since {formatDate(config.effectiveFrom, { withTime: true })}
            </p>
          </div>
          <Link
            to="/hr/performance"
            search={{}}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
          >
            <Settings size={12} aria-hidden="true" />
            Edit config
          </Link>
        </header>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Period" value={`${config.periodDays} days`} />
          <Stat
            label="Warming-up tenure"
            value={`${config.warmingUpTenureDays} days`}
          />
          <Stat
            label="Min active days"
            value={`${config.minActiveDaysForBand} days`}
          />
          <Stat
            label="Fee grace period"
            value={`${config.feeGracePeriodDays} days`}
          />
          <Stat
            label="Referral cap"
            value={config.referralSaturationCap.toString()}
          />
          <Stat label="Top threshold" value={`${config.thresholds.top}`} />
          <Stat label="Medium threshold" value={`${config.thresholds.medium}`} />
          <Stat
            label="Variables included"
            value={`${includedKeys.length} of 6`}
          />
        </dl>
      </article>
      <article className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="mb-3 font-display text-sm font-semibold">Weights</h3>
        <dl className="grid gap-1.5 text-xs sm:grid-cols-2">
          {Object.entries(config.weights).map(([key, weight]) => (
            <div
              key={key}
              className="flex items-baseline justify-between gap-3 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-1"
            >
              <dt className="capitalize text-[color:var(--color-text-muted)]">
                {key.replace(/_/g, ' ')}
              </dt>
              <dd className="font-semibold tabular-nums">
                {(weight * 100).toFixed(0)}%
              </dd>
            </div>
          ))}
        </dl>
      </article>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: ReactNode;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </p>
      <p className="mt-0.5 font-display text-sm font-semibold tabular-nums">
        {value}
      </p>
    </div>
  );
}

function AxisStat({
  label,
  value,
}: {
  label: string;
  value: number | null;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-1">
      <dt className="text-[color:var(--color-text-muted)]">{label}</dt>
      <dd className="font-semibold tabular-nums">
        {value === null ? '—' : value.toFixed(1)}
      </dd>
    </div>
  );
}

interface OverflowItem {
  label: string;
  icon?: JSX.Element;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  items: OverflowItem[];
}

function OverflowMenu({
  open,
  onOpenChange,
  items,
}: OverflowMenuProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
      >
        More
      </Button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <ul
            role="menu"
            className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-lg"
          >
            {items.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-[color:var(--color-surface)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.danger && 'text-[color:var(--color-danger)]',
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
